import { useRef, useState, useEffect } from 'react';
import { Dropdown, Menu } from 'antd';
import { getZoom } from '@/utils/scaleWindow';
import { AdvanceSvg, PlaySvg, PauseSvg } from '../../Svg';
import style from './index.module.less';

interface IProps {
  src: string;
  playStateCallBack: (val: boolean) => void; 
  recordAudioProgress: {duration: number; currentTime: number;};
  saveRecordProgress: (val: {duration: number; currentTime: number;}) => void;
}

const AudioPlayer = ({ src, playStateCallBack, recordAudioProgress, saveRecordProgress }: IProps) => {

  const audioRef = useRef<HTMLAudioElement| null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<string>('1.0X');
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    playStateCallBack(isPlaying);
  }, [isPlaying]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(err => {
          setIsPlaying(false);
          console.log('音频播放错误3', err);
        });
        
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 10;

    }
  };

  const handleBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 10;
    }
  };

  const handleProgress = () => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      const progressPercent = (currentTime / duration) * 100;
      setCurrentTime(currentTime);
      setProgress(progressPercent);
      if (duration) {
        saveRecordProgress({currentTime: parseFloat(currentTime.toFixed(3)), duration});
      }
      
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
      setIsPlaying(false);
      const {currentTime = 0, duration = 0} = recordAudioProgress || {};  
      setProgress(duration === 0 ? 0 : currentTime/duration);
      audioRef.current.currentTime = currentTime;
      setCurrentTime(currentTime);
      audioRef.current.playbackRate = parseFloat(playbackRate);
    }
  }, [src]);


  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handleThumbMouseDown = () => {
    setIsDragging(true);
  };

  const handleDocumentMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
    }
  };

  const handleDocumentMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      if (progressRef.current && audioRef.current) {
        const rect = progressRef.current.getBoundingClientRect();
        const zoom = getZoom();
        const offsetX = e.clientX / zoom - rect.left;
        const radio = Math.max(Math.min(offsetX / rect.width, 1), 0);
        audioRef.current.currentTime = radio * audioRef.current.duration;
        setProgress(radio * 100);
      }
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const loadedmetadataHandle = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      document.addEventListener('mousemove', handleDocumentMouseMove);
      document.addEventListener('mouseup', handleDocumentMouseUp);
    }

    return () => {
      if (audioRef.current) {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      }
    };
  }, [isDragging]);

  const handleMenuClick = (e: {key: string;}) => {
    if (audioRef.current) {
      const rate = parseFloat(e.key);
      setPlaybackRate(e.key);
      audioRef.current.playbackRate = rate;
    }
  };

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="0.5X">0.5X</Menu.Item>
      <Menu.Item key="1.0X">1.0X</Menu.Item>
      <Menu.Item key="1.5X">1.5X</Menu.Item>
      <Menu.Item key="2.0X">2.0X</Menu.Item>
    </Menu>
  );

  return (
    <div className={style.audioPlayer}>
      <audio ref={audioRef} className={style.audioEl} id="audio" src={src} onPlay={handlePlay} onEnded={handleEnded}
        onLoadedMetadata={loadedmetadataHandle} onTimeUpdate={handleProgress} onPause={handleEnded}>
      </audio>
      <div className={style.controls}>
        <div onClick={handleBackward} className={style.back}>{AdvanceSvg()}</div>
        <div onClick={togglePlayPause} className={style.playBtn}>{isPlaying ? PauseSvg() : PlaySvg()}</div>
        <div onClick={handleForward} className={style.advance}>{AdvanceSvg()}</div>

        <div className={style.progressBar} ref={progressRef} onClick={(e) => {
          if (audioRef.current && progressRef.current) {
            const rect = progressRef.current.getBoundingClientRect();
            const offsetX = e.clientX / getZoom() - rect.left;
            const newProgress = (offsetX / rect.width) * audioRef.current.duration;
            audioRef.current.currentTime = newProgress;
          }
        }}>
          <div className={style.progress} style={{ width: `${progress}%` }}></div>
          <div className={style.thumb} style={{ left: `${progress}%` }} onMouseDown={handleThumbMouseDown}></div>
        </div>
        <div className={style.timeInfo}>
          <span className={style.currentTime}>{formatTime(currentTime)}</span>/
          <span className={style.duration}>{formatTime(duration)}</span>
        </div>
        <Dropdown overlay={menu} trigger={['click']}>
          <div className={style.radio}>{`${playbackRate}`}</div>
        </Dropdown>
      </div>
    </div>
  );
};

export default AudioPlayer;
